import { gql } from '@apollo/client';

/**
 * Spec 0018 (career center), increment A — ERP-scope mutations.
 *
 * Operation names differ from the backend field names
 * (`enrollCareerMembers`, `updateCareerMember`, `upsertCareerLesson`) to avoid
 * the codegen result-type collapse. All three return the project envelope
 * (`success` + `customErrors`).
 */

/**
 * Bulk enrolment. `studentIds` and `groupId` are additive — the backend unions
 * them — and re-running is safe: an already-enrolled student lands in
 * `skipped`, an archived one in `reactivated`. The three counters drive the
 * result toast.
 */
export const EnrollCareerMembersMutation = gql`
  mutation EnrollToCareerCenter(
    $studentIds: [ID!]
    $groupId: ID
    $directionId: ID
  ) {
    enrollCareerMembers(
      studentIds: $studentIds
      groupId: $groupId
      directionId: $directionId
    ) {
      success
      customErrors {
        field
        message
        code
      }
      created
      reactivated
      skipped
    }
  }
`;

/**
 * Убрать из карьерного центра или вернуть обратно (ТЗ 0039, доводка).
 *
 * 🔴 Это НЕ удаление: пропадает только доступ, а прогресс, отклики и запись о
 * трудоустройстве остаются. Массовая, потому что оператор чистит ошибочно
 * добавленную группу целиком — цикл из мутаций дал бы столько же полных
 * перезагрузок списка (тот же довод, что у `BulkSetVacancyStatus`).
 */
export const SetCareerMembersAccessMutation = gql`
  mutation SetCareerMembersAccess($memberIds: [ID!]!, $removed: Boolean!) {
    setCareerMembersAccess(memberIds: $memberIds, removed: $removed) {
      success
      customErrors {
        code
        field
        message
      }
      changed
      unchanged
      missing
    }
  }
`;

export const UpdateCareerMemberMutation = gql`
  mutation SaveCareerMember($id: ID!, $input: CareerMemberInput!) {
    updateCareerMember(id: $id, input: $input) {
      success
      customErrors {
        field
        message
        code
      }
      member {
        id
        studentId
        studentName
        studentEmail
        groupId
        groupName
        directionId
        directionTitle
        status
        enrolledAt
        notes
        lessonsCompleted
        lessonsTotal
      }
    }
  }
`;

/**
 * Create (no `id`) or update (`id`) a lesson of the single career track. The
 * returned `lesson` selection mirrors `ErpCareerLessons` field-for-field
 * (spec 0020 added `videoUrl` + `attachments`, then `stats` + `quizQuestions`)
 * so `saveLesson` can hand the saved row — freshly minted with an `id` —
 * straight back to the form, which then unlocks the video/attachment/quiz
 * controls. The selection must stay in lock-step with the query: `saveLesson`
 * is typed to return the full `ICareerLesson`.
 */
export const UpsertCareerLessonMutation = gql`
  mutation SaveCareerLesson($id: ID, $input: CareerLessonInput!) {
    upsertCareerLesson(id: $id, input: $input) {
      success
      customErrors {
        field
        message
        code
      }
      lesson {
        id
        order
        title
        summary
        content
        durationMin
        completionKind
        artifactHint
        isPublished
        videoUrl
        attachments {
          id
          originalName
          contentType
          sizeBytes
          url
        }
        stats {
          openedCount
          completedCount
          membersTotal
        }
        quizQuestions {
          id
          order
          text
          options
          correctIndexes
        }
      }
    }
  }
`;

/* ── Spec 0020 — lesson authoring parity: video + attachments ─────────── */

/**
 * Attach (or replace) the lesson's video in a single multipart request: the
 * picked `File` travels as an `Upload` scalar over the GraphQL multipart request
 * spec (same terminating `createUploadLink` as `uploadStudentDiploma`), and the
 * backend writes the object server-side — no presigned PUT, no bucket CORS. The
 * returned `lesson` echoes the fresh media so the form swaps its local state
 * wholesale.
 *
 * Operation names throughout this block differ from the backend field names
 * (`uploadCareerLessonVideo`, `clearCareerLessonVideo`,
 * `addCareerLessonAttachment`, `removeCareerLessonAttachment`) to avoid the
 * codegen result-type collapse.
 */
export const UploadCareerLessonVideoMutation = gql`
  mutation AttachCareerLessonVideo($lessonId: ID!, $video: Upload!) {
    uploadCareerLessonVideo(lessonId: $lessonId, video: $video) {
      success
      customErrors {
        field
        message
        code
      }
      lesson {
        id
        videoUrl
        attachments {
          id
          originalName
          contentType
          sizeBytes
          url
        }
      }
    }
  }
`;

/** Detach the lesson's video. */
export const ClearCareerLessonVideoMutation = gql`
  mutation RemoveCareerLessonVideo($lessonId: ID!) {
    clearCareerLessonVideo(lessonId: $lessonId) {
      success
      customErrors {
        field
        message
        code
      }
      lesson {
        id
        videoUrl
        attachments {
          id
          originalName
          contentType
          sizeBytes
          url
        }
      }
    }
  }
`;

/**
 * Attach one file. `file` travels as an `Upload` scalar over the GraphQL
 * multipart request spec (same terminating `createUploadLink` as
 * `uploadStudentDiploma`). The returned `lesson.attachments` is the full,
 * updated list — the form replaces its local list with it wholesale.
 */
export const AddCareerLessonAttachmentMutation = gql`
  mutation AttachCareerLessonFile($lessonId: ID!, $file: Upload!) {
    addCareerLessonAttachment(lessonId: $lessonId, file: $file) {
      success
      customErrors {
        field
        message
        code
      }
      attachment {
        id
        originalName
        contentType
        sizeBytes
        url
      }
      lesson {
        id
        videoUrl
        attachments {
          id
          originalName
          contentType
          sizeBytes
          url
        }
      }
    }
  }
`;

/** Remove one attachment by id (returns only the `ok` flag — no lesson). */
export const RemoveCareerLessonAttachmentMutation = gql`
  mutation DetachCareerLessonFile($attachmentId: ID!) {
    removeCareerLessonAttachment(attachmentId: $attachmentId) {
      success
      ok
      customErrors {
        field
        message
        code
      }
    }
  }
`;

/* ── Spec 0020 Фаза C — lesson quiz editor (ERP scope, CAREER_ROLES) ──── */

/**
 * Replace the whole quiz for one lesson. `questions` is the full desired set —
 * the backend swaps the lesson's questions for it wholesale, and `order` follows
 * array position. Each question needs ≥2 options and ≥1 correct index; a
 * violation comes back as a `customErrors` field entry (surfaced by the form),
 * not a throw.
 *
 * The wire input field is `correctIndexes` (Graphene camel-cases the Python
 * `correct_indexes`); the returned `quizQuestions` selection matches
 * `ErpCareerLessons` field-for-field. Op name differs from the
 * `setCareerLessonQuiz` field to avoid the codegen result-type collapse.
 */
export const SetCareerLessonQuizMutation = gql`
  mutation SaveCareerLessonQuiz(
    $lessonId: ID!
    $questions: [CareerQuizQuestionInput!]!
  ) {
    setCareerLessonQuiz(lessonId: $lessonId, questions: $questions) {
      success
      customErrors {
        field
        message
        code
      }
      lesson {
        id
        quizQuestions {
          id
          order
          text
          options
          correctIndexes
        }
      }
    }
  }
`;

/* ── Increment B — vacancies + placement ─────────────────────────────── */

const VACANCY_FIELDS = gql`
  fragment ErpVacancyFields on VacancyType {
    id
    title
    companyName
    applyUrl
    location
    workMode
    seniority
    employmentType
    salaryMin
    salaryMax
    salaryCurrency
    description
    status
    publishedAt
    expiresAt
    createdAt
    directionIds
    directionTitles
    skillIds
    skillNames
    appliedCount
    source
    createdByName
    reviewedByName
    reviewedAt
    updatedAt
    duplicateOf {
      vacancyId
      title
      companyName
      status
      score
    }
  }
`;

/**
 * Create (no `id`) or update (`id`) a vacancy. A new vacancy is born DRAFT;
 * status is moved separately through `SetVacancyStatus`. Operation name differs
 * from the `upsertVacancy` field to keep a dedicated result type.
 */
export const UpsertVacancyMutation = gql`
  mutation UpsertVacancy($id: ID, $input: VacancyInput!) {
    upsertVacancy(id: $id, input: $input) {
      success
      customErrors {
        field
        message
        code
      }
      vacancy {
        ...ErpVacancyFields
      }
    }
  }
  ${VACANCY_FIELDS}
`;

/**
 * Move one vacancy along the DRAFT→APPROVED→PUBLISHED→REJECTED→ARCHIVED
 * workflow. `status` is a plain String on the wire.
 */
/**
 * Hard-delete a vacancy. The backend allows it only for DRAFT/REJECTED rows
 * with no placement attached — everything else refuses with `vacancy_locked`
 * and points at the archive, because `VacancyInteraction` cascades and would
 * take students' saved marks and the funnel's applied history with it.
 * `deletedInteractions` is what the confirm dialog shows as the cost.
 */
export const DeleteVacancyMutation = gql`
  mutation DeleteVacancy($id: ID!) {
    deleteVacancy(id: $id) {
      success
      ok
      deletedInteractions
      customErrors {
        field
        message
        code
      }
    }
  }
`;

export const SetVacancyStatusMutation = gql`
  mutation SetVacancyStatus($id: ID!, $status: String!) {
    setVacancyStatus(id: $id, status: $status) {
      success
      customErrors {
        field
        message
        code
      }
      vacancy {
        ...ErpVacancyFields
      }
    }
  }
  ${VACANCY_FIELDS}
`;

/** Publish many drafts at once; `published` is the count actually flipped. */
export const BulkPublishVacanciesMutation = gql`
  mutation BulkPublishVacancies($ids: [ID!]!) {
    bulkPublishVacancies(ids: $ids) {
      success
      customErrors {
        field
        message
        code
      }
      published
    }
  }
`;

/** Record a graduate placement (the centre's KPI). */
export const RecordPlacementMutation = gql`
  mutation RecordPlacement($memberId: ID!, $input: PlacementInput!) {
    recordPlacement(memberId: $memberId, input: $input) {
      success
      customErrors {
        field
        message
        code
      }
      placement {
        id
        memberId
        studentName
        companyName
        position
        startedAt
        confirmedAt
      }
    }
  }
`;

/**
 * Move many vacancies at once (spec 0035) — the queue-draining action.
 *
 * `BulkPublishVacancies` only ever published, so rejecting a night's agent
 * drafts meant one mutation per row, each triggering a full table reload.
 * This covers every status and is set-based on the server.
 */
export const BulkSetVacancyStatusMutation = gql`
  mutation BulkSetVacancyStatus($ids: [ID!]!, $status: String!) {
    bulkSetVacancyStatus(ids: $ids, status: $status) {
      success
      moved
      customErrors {
        field
        message
        code
      }
    }
  }
`;

/** ТЗ 0039 — завести или поправить навык справочника. */
export const UpsertCareerSkillMutation = gql`
  mutation SaveCareerSkill($input: CareerSkillInput!) {
    upsertCareerSkill(input: $input) {
      success
      skill {
        id
        name
        slug
        category
        description
        isActive
      }
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/**
 * ТЗ 0039 — спрятать навык из автокомплита или вернуть.
 *
 * Мутации УДАЛЕНИЯ навыка не существует и не должно: `Skill.delete()`
 * каскадит `StudentSkill`, то есть стирает навык из резюме всех владельцев,
 * и вычищает теги вакансий.
 */
export const SetCareerSkillActiveMutation = gql`
  mutation ToggleCareerSkill($id: ID!, $isActive: Boolean!) {
    setCareerSkillActive(id: $id, isActive: $isActive) {
      success
      skill {
        id
        isActive
      }
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/**
 * ТЗ 0039 доводка — своя картинка навыка либо возврат к автоподбору.
 *
 * Одна мутация на оба действия: «убрать» здесь не оставляет пустоту, а
 * возвращает встроенный знак по слагу (или буквенную плашку). Разводить это
 * на две операции значило бы делать вид, что у навыка бывает «нет иконки».
 */
export const SetCareerSkillIconMutation = gql`
  mutation SaveCareerSkillIcon($id: ID!, $icon: Upload, $clear: Boolean) {
    setCareerSkillIcon(id: $id, icon: $icon, clear: $clear) {
      success
      skill {
        id
        iconUrl
      }
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/** ТЗ 0039 — слить дубль справочника («React» и «ReactJS»). */
export const MergeCareerSkillsMutation = gql`
  mutation JoinCareerSkills($sourceId: ID!, $targetId: ID!) {
    mergeCareerSkills(sourceId: $sourceId, targetId: $targetId) {
      success
      movedStudentSkills
      movedVacancies
      movedProjects
      movedCourseSkills
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/**
 * ТЗ 0041 — задать набор навыков ГРУППЫ целиком.
 *
 * Занятие передаётся ссылкой (`unlockLessonId`), а не номером: у группы оно
 * существует строкой в базе, и ссылка переживает перенос, переименование и
 * вставку соседнего занятия. Пусто — навык придёт по завершении курса.
 */
export const SetGroupSkillsMutation = gql`
  mutation SaveGroupSkills($groupId: ID!, $rows: [GroupSkillInput!]!) {
    setGroupSkills(groupId: $groupId, rows: $rows) {
      success
      added
      updated
      deactivated
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/** ТЗ 0039 — задать набор навыков курса целиком (ШАБЛОН для новых групп). */
export const SetCourseSkillsMutation = gql`
  mutation SaveCourseSkills($courseId: ID!, $rows: [CourseSkillInput!]!) {
    setCourseSkills(courseId: $courseId, rows: $rows) {
      success
      added
      updated
      deactivated
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/** ТЗ 0039 — ручное «зачтено / не засчитывать» по паре студент-курс. */
export const SetCourseCompletionMutation = gql`
  mutation SaveCourseCompletion(
    $studentId: ID!
    $courseId: ID!
    $status: String!
    $note: String
  ) {
    setCourseCompletion(
      studentId: $studentId
      courseId: $courseId
      status: $status
      note: $note
    ) {
      success
      completedCourseIds
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/** ТЗ 0039 — кнопка «Пересчитать навыки» на карточке участника. */
export const SyncStudentCourseSkillsMutation = gql`
  mutation RecalcStudentCourseSkills($studentId: ID!) {
    syncStudentCourseSkills(studentId: $studentId) {
      success
      granted
      promoted
      demoted
      unchanged
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/** ТЗ 0039 — собрать анкету урока. */
export const SaveCareerExerciseTemplateMutation = gql`
  mutation SaveCareerExerciseTemplate(
    $templateId: ID
    $title: String!
    $intro: String
    $outro: String
    $fields: [CareerExerciseFieldInput!]!
  ) {
    saveCareerExerciseTemplate(
      templateId: $templateId
      title: $title
      intro: $intro
      outro: $outro
      fields: $fields
    ) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

export const ToggleCareerExerciseTemplateMutation = gql`
  mutation ToggleCareerExerciseTemplate($templateId: ID!, $isActive: Boolean!) {
    toggleCareerExerciseTemplate(templateId: $templateId, isActive: $isActive) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

export const AttachCareerExerciseTemplateMutation = gql`
  mutation AttachCareerExerciseTemplate($lessonId: ID!, $templateId: ID!) {
    attachCareerExerciseTemplate(lessonId: $lessonId, templateId: $templateId) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

export const DetachCareerExerciseTemplateMutation = gql`
  mutation DetachCareerExerciseTemplate($lessonId: ID!) {
    detachCareerExerciseTemplate(lessonId: $lessonId) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

export const DropCareerExerciseSubmissionsMutation = gql`
  mutation DropCareerExerciseSubmissions($templateId: ID!, $confirm: Boolean!) {
    resetCareerExerciseTemplate(templateId: $templateId, confirm: $confirm) {
      success
      deletedSubmissions
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/** ТЗ 0039 шаг 6 — завести или поправить акцию. */
export const UpsertPromoCampaignMutation = gql`
  mutation SavePromoCampaign($input: PromoCampaignInput!) {
    upsertPromoCampaign(input: $input) {
      success
      campaignId
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/** Включить/выключить акцию. Удаления нет: коды уже у людей на руках. */
export const SetPromoCampaignActiveMutation = gql`
  mutation TogglePromoCampaign($id: ID!, $isActive: Boolean!) {
    setPromoCampaignActive(id: $id, isActive: $isActive) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/** Отозвать код. */
/**
 * ТЗ 0039 доводка — выдать код студенту руками.
 *
 * 🔴 Мутация на бэкенде была с самого начала и открыта ERP-скоупу, а фронтовой
 * операции для неё не существовало. То есть акцию «только вручную» можно было
 * создать, но выдать по ней код из ERP — нельзя: половина функции была
 * недоступна, и понять это можно было только полезши в схему.
 */
export const IssuePromoCodeMutation = gql`
  mutation GivePromoCode($campaignId: ID!, $studentId: ID!) {
    issuePromoCode(campaignId: $campaignId, studentId: $studentId) {
      success
      code
      customErrors {
        code
        field
        message
      }
    }
  }
`;

export const RevokePromoCodeMutation = gql`
  mutation DropPromoCode($id: ID!, $reason: String) {
    revokePromoCode(id: $id, reason: $reason) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/**
 * ТЗ 0039 шаг 6 — кнопка «Проверить сейчас».
 *
 * Та же сверка, что идёт утром задачей. Денег не касается: помечает коды
 * использованными и ничего не меняет ни в договорах, ни во взносах.
 */
export const ReconcilePromoCodesMutation = gql`
  mutation CheckPromoCodes {
    reconcilePromoCodes {
      success
      checked
      redeemed
      ambiguous
      expired
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/** ТЗ 0039 доводка — текст поста правится без выката. */
export const UpsertShareTemplateMutation = gql`
  mutation SaveShareTemplate($input: ShareTemplateInput!) {
    upsertShareTemplate(input: $input) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/* ── ТЗ 0040 фаза C: справочники роста и витрины стали правимыми ────────── */

export const SaveGrowthTrackMutation = gql`
  mutation SaveGrowthTrack($input: GrowthTrackInput!) {
    saveGrowthTrack(input: $input) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

export const ToggleGrowthTrackMutation = gql`
  mutation ToggleGrowthTrack($trackId: ID!, $isActive: Boolean!) {
    toggleGrowthTrack(trackId: $trackId, isActive: $isActive) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

export const SaveAchievementMutation = gql`
  mutation SaveAchievement($input: AchievementInput!) {
    saveAchievement(input: $input) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

export const ToggleAchievementMutation = gql`
  mutation ToggleAchievement($achievementId: ID!, $isActive: Boolean!) {
    toggleAchievement(achievementId: $achievementId, isActive: $isActive) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

export const SaveSiteCourseMutation = gql`
  mutation SaveSiteCourse($input: SiteCourseInput!) {
    saveSiteCourse(input: $input) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

export const ToggleSiteCourseMutation = gql`
  mutation ToggleSiteCourse($siteCourseId: ID!, $isActive: Boolean!) {
    toggleSiteCourse(siteCourseId: $siteCourseId, isActive: $isActive) {
      success
      customErrors {
        code
        field
        message
      }
    }
  }
`;

/**
 * ТЗ 0040 фаза C — правка программы курса.
 *
 * 🔴 `lessonsRemoved` в ответе — не украшение: занятие, присланное БЕЗ `id`,
 * считается новым, а старое на его месте удаляется вместе с материалами.
 * Экран обязан показать это число, иначе потеря пройдёт молча.
 */
export const SetCourseProgramMutation = gql`
  mutation SetCourseProgram($courseId: ID!, $modules: [ProgramModuleInput!]!) {
    setCourseProgram(courseId: $courseId, modules: $modules) {
      success
      modules
      lessons
      lessonsRemoved
      lessonsWithoutMarketing
      customErrors {
        code
        field
        message
      }
    }
  }
`;
